import { createSelector } from 'reselect'
import { getPageData, getPageParams } from 'selectors/page'
import {
  getAlgoliaDataPaginationCurrent,
  getAlgoliaDataPaginationTotal,
} from 'selectors/algolia2/data'
import {
  getIsAdmin,
  getUserID,
  isAuthenticated as getIsAuthenticated,
} from 'selectors/user/session'
import { getUserPreferencesCurrency } from 'selectors/user/cookie'
import { capitalizeString } from 'lib/helpers'

export { getIsAdmin } from 'selectors/user/session'
export { getPageIsReady, getPageData, getPageParams, getPagePath } from 'selectors/page'
export const getProfileData = createSelector(
  [getPageData],
  (pageData): number => pageData.profileData || {}
)
export const getUserId = createSelector(
  [getProfileData],
  (profileData): number => profileData.userId || 0
)
export const getAvatarUrl = createSelector(getProfileData, (data): string => {
  const tinyAvatar = data.avatar.replace('medium', 'tiny')
  return tinyAvatar || '//d3t95n9c6zzriw.cloudfront.net/common/profile/avatar_tiny_square.jpg'
})
export const getArtistName = createSelector(
  getProfileData,
  (data): string => `${data.firstName} ${data.lastName}` || ''
)
export const getArtistCountry = createSelector(
  [getProfileData],
  (pageData): number => pageData.country || ''
)
export const getCollectionsTotal = createSelector(
  getProfileData,
  (data): number => data.collectionsTotal || 0
)
export const getArtworksCount = createSelector(
  getProfileData,
  (data): number => data.artworksCount || 0
)
export const getFavoritesTotal = createSelector(
  getProfileData,
  (data): number => data.favoritesTotal || 0
)
export const getIsUsersCollectionPage = createSelector(
  [getPageParams, getUserID, getIsAuthenticated],
  (params, userId, isAuthenticated): boolean =>
    (isAuthenticated && Number(userId) === Number(params.userId || 0)) || false
)
export const getHasValidPermissions = createSelector(
  [getIsUsersCollectionPage, getIsAdmin],
  (isUsersCollectionPage, isAdmin): boolean => isUsersCollectionPage || isAdmin || false
)
export const getMetaData = createSelector(
  [
    getAvatarUrl,
    getArtistCountry,
    getArtistName,
    getAlgoliaDataPaginationCurrent,
    getUserPreferencesCurrency,
    getAlgoliaDataPaginationTotal,
  ],
  (
    avatarUrl,
    artistCountry,
    artistName,
    currentPage,
    selectedLocale,
    totalPages
  ): Record<string, any> => {
    const capitalizedArtistName = capitalizeString(artistName)
    // TODO - METADATA - PREV/NEXT urlWithoutParams: (state) => urlWithoutParams(state)
    const description = `View ${capitalizedArtistName}'s art collections on Saatchi Art. Find collections of original art for sale at great prices, including Paintings, Sculptures, Photography, Drawings, and Art Prints from emerging artists around the world.`
    const title = `${capitalizedArtistName}'s Art Collections | Saatchi Art`
    return {
      breadcrumbPaths: ['Account/Artworks'],
      // TODO - METADATA - This is wrong? Collections?
      description,
      title,
      og: {
        description,
        image: avatarUrl,
        locale: selectedLocale,
        title,
        type: 'article',
      },
      page: {
        current: currentPage,
        prev: currentPage === 2 || currentPage > 2,
        next: currentPage !== totalPages,
      },
      preloadImages: [],
      robots: false,
      schema: {
        webpage: {
          description,
          headline: title,
        },
      },
    }
  }
)
export const getDatalayer = createSelector(getPageData, (data) => {
  const layerData = {
    pagetype: 'artistProfile',
    subpagetype: 'artworks',
    subsubpagetype: '',
  }
  layerData.subsubpagetype = `${data.firstName} ${data.lastName}` || ''
  return layerData
})
